import { ReactNode, useState } from 'react';

import { format } from 'date-fns';

//TODO: fsd
import { ComplianceBar, SpeedUpBar } from '@/features/compliance';

import { AddToFavorites } from '@/features/favorite-payment/ui';

import { ITransaction } from '@/entities/transaction';

import {
  VabsParticipantType,
  VabsPaymentDirection,
  VabsPaymentStatus,
  VabsPaymentSystem,
} from '@/shared/api';
import { formatAmountValue, getValueWithCurrency } from '@/shared/lib';
import { shortText } from '@/shared/lib/utils/text';
import { DashboardDialog } from '@/shared/ui';

import { FINAL_STATUSES } from '../../config';
import { TransactionDetailGroup } from './group';
import { TransactionDetailHead } from './head';
import { Status } from './status';
import { FIELD_NAMES } from '@/shared/config/configs';

const RECIPIENT_TYPE = {
  [VabsParticipantType.B2B]: 'Business',
  [VabsParticipantType.B2C]: 'Private',
  [VabsParticipantType.C2B]: 'Business',
  [VabsParticipantType.C2C]: 'Private',
  [VabsParticipantType.Self]: 'Self',
};

interface TransactionsDetailsModalProps {
  children: (onClick: () => void) => ReactNode;
  transaction: ITransaction;
}

const TopUpContent = (tx: ITransaction) => {
  return (
    <div className="flex flex-col gap-y-1">
      <TransactionDetailGroup>
        <TransactionDetailGroup.Item className="whitespace-nowrap" title="When">
          {format(tx.date, "dd.MM.yyyy 'at' h:mm aa")}
        </TransactionDetailGroup.Item>
      </TransactionDetailGroup>
      <TransactionDetailGroup>
        <TransactionDetailGroup.Item title="You sent">
          {getValueWithCurrency(
            formatAmountValue(tx.sourceTotalAmount),
            tx.sourceCurrency,
          )}
        </TransactionDetailGroup.Item>

        <TransactionDetailGroup.Item title="Our fees">
          {getValueWithCurrency(tx.fee, tx.sourceCurrency)}
        </TransactionDetailGroup.Item>

        <TransactionDetailGroup.Item title="You received">
          {getValueWithCurrency(
            formatAmountValue(tx.targetAmount),
            tx.targetCurrency,
          )}
        </TransactionDetailGroup.Item>
      </TransactionDetailGroup>
    </div>
  );
};

const SendContent = (tx: ITransaction) => {
  const isInner = tx.paymentSystem === VabsPaymentSystem.Inner;
  return (
    <div className="flex flex-col gap-y-1">
      {FINAL_STATUSES.includes(tx.status) && (
        <TransactionDetailGroup>
          <TransactionDetailGroup.Item
            className="whitespace-nowrap"
            title="When"
          >
            {format(tx.date, "dd.MM.yyyy 'at' h:mm aa")}
          </TransactionDetailGroup.Item>
        </TransactionDetailGroup>
      )}
      <TransactionDetailGroup>
        <TransactionDetailGroup.Item title="You sent">
          {getValueWithCurrency(
            formatAmountValue(tx.sourceTotalAmount),
            tx.sourceCurrency,
          )}
        </TransactionDetailGroup.Item>

        <TransactionDetailGroup.Item title="Our fees">
          {getValueWithCurrency(tx.fee, tx.sourceCurrency)}
        </TransactionDetailGroup.Item>
      </TransactionDetailGroup>
      <TransactionDetailGroup>
        <TransactionDetailGroup.Item title="Recipient type">
          {isInner ? 'Inner' : RECIPIENT_TYPE[tx.recipientType]}
        </TransactionDetailGroup.Item>
        {!isInner && (
          <>
            <TransactionDetailGroup.Item title={FIELD_NAMES[tx.paymentSystem].bankCode}>
              {tx.bankCode}
            </TransactionDetailGroup.Item>
            <TransactionDetailGroup.Item title={FIELD_NAMES[tx.paymentSystem].accountNumber}>
              {tx.accountNumber}
            </TransactionDetailGroup.Item>
          </>
        )}
        <TransactionDetailGroup.Item title="Account holder name">
          {shortText((isInner ? tx.name : tx.accountHolder) || '', 24)}
        </TransactionDetailGroup.Item>
        {!isInner && (
          <TransactionDetailGroup.Item title="Bank name">
            {tx.bankName}
          </TransactionDetailGroup.Item>
        )}
      </TransactionDetailGroup>
      <TransactionDetailGroup>
        <AddToFavorites id={tx.id} isFav={tx.isFav} />
      </TransactionDetailGroup>
    </div>
  );
};

const ExchangeContent = (tx: ITransaction) => {
  const paidFrom = `${tx.sourceCurrency} account`;
  const exchangeRate = tx.sourceExchangeRate
    ? formatAmountValue(tx.sourceExchangeRate)
    : '0';
  return (
    <div className="flex flex-col gap-y-1">
      {FINAL_STATUSES.includes(tx.status) && (
        <TransactionDetailGroup>
          <TransactionDetailGroup.Item
            className="whitespace-nowrap"
            title="When"
          >
            {format(tx.date, "dd.MM.yyyy 'at' h:mm aa")}
          </TransactionDetailGroup.Item>
        </TransactionDetailGroup>
      )}
      <TransactionDetailGroup>
        <TransactionDetailGroup.Item title="Paid from">
          {paidFrom}
        </TransactionDetailGroup.Item>

        <TransactionDetailGroup.Item title="Amount taken">
          {getValueWithCurrency(
            formatAmountValue(tx.sourceTotalAmount),
            tx.sourceCurrency,
          )}
        </TransactionDetailGroup.Item>

        <TransactionDetailGroup.Item title="Our conversion fee">
          {getValueWithCurrency(tx.fee, tx.sourceCurrency)}
        </TransactionDetailGroup.Item>

        <TransactionDetailGroup.Item title="We converted">
          {getValueWithCurrency(
            formatAmountValue(tx.sourceAmount),
            tx.sourceCurrency,
          )}
        </TransactionDetailGroup.Item>

        <TransactionDetailGroup.Item title="Exchange rate">
          1 {tx.sourceCurrency} = {getValueWithCurrency(exchangeRate, tx.targetCurrency)}
        </TransactionDetailGroup.Item>

        <TransactionDetailGroup.Item title="Converted to">
          {getValueWithCurrency(
            formatAmountValue(tx.targetAmount),
            tx.targetCurrency,
          )}
        </TransactionDetailGroup.Item>
      </TransactionDetailGroup>
    </div>
  );
};

const Content = ({
  tx,
  onClose,
}: {
  tx: ITransaction;
  onClose: () => void;
}) => {
  return (
    <div className="flex flex-1 flex-col">
      <div className="max-h-[536px]">
        <TransactionDetailHead tx={tx} onClose={onClose} />
        {tx.status === VabsPaymentStatus.NeedAction && (
          <ComplianceBar paymentId={tx.id} variant="transaction" onAdd={onClose} />
        )}
        {tx.type === VabsPaymentDirection.Outgoing && tx.status === VabsPaymentStatus.Processing && !tx.note && (
          <SpeedUpBar paymentId={tx.id} variant="transaction" onAdd={onClose} />
        )}
        {!FINAL_STATUSES.includes(tx.status) && <Status {...tx} />}
        {tx.paymentSystem === VabsPaymentSystem.Exchange ? (
          <ExchangeContent {...tx} />
        ) : (
          <>
            {tx.type === VabsPaymentDirection.Incoming && <TopUpContent {...tx} />}
            {tx.type === VabsPaymentDirection.Outgoing && <SendContent {...tx} />}
          </>
        )}
      </div>
    </div>
  );
};

export const TransactionsDetailsModal = ({
  children,
  transaction,
}: TransactionsDetailsModalProps) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <DashboardDialog
      title=""
      content={
        <Content
          tx={transaction}
          onClose={() => {
            setIsOpen(false);
          }}
        />
      }
      animationProps={{ isFirst: true }}
      height="fixed"
      triggerProps={{ asChild: true }}
      open={isOpen}
      onOpenChange={setIsOpen}
      onClose={() => {
        setIsOpen(false);
      }}
    >
      {children(() => {
        setIsOpen(true);
      })}
    </DashboardDialog>
  );
};
